import { useMemo } from 'react';
import type { GuardianAgent } from '../components/message-composer/chat-context/selectors';
import { useAgents } from './useAgents';

/** Backend agent name of the agents (agents_config.json). */
export const ANALYTICS_AGENT_ID = 'analytics';
export const CUSTOMER_SUPPORT_AGENT_ID = 'customer_support_agent';

export const AGENT_ICON: Record<string, string> = {
  [ANALYTICS_AGENT_ID]: 'Metrics',
  documentation: 'Bookopen',
  cve: 'Protected',
  troubleshooting: 'HeadSet',
  procedure: 'Book',
  maintenance: 'Maintenance',
  aiops_agent: 'Toolbox',
  opal_agent: 'Opal',
  [CUSTOMER_SUPPORT_AGENT_ID]: 'Support',
};

export type UseAgentItemsResult = {
  agentItems: GuardianAgent[];
  status: 'pending' | 'success' | 'error';
  findAgent: (id: string) => GuardianAgent | undefined;
};

/**
 * Fetches the agents list and folds it into the parent/child tree the selector
 * expects. Shared by the chat contextualizer and the dashboard-open flow so the
 * folding/icon logic lives in one place.
 */
export const useAgentItems = (): UseAgentItemsResult => {
  const { data: agentsData, status } = useAgents();

  const flat: GuardianAgent[] = useMemo(() => {
    if (!agentsData?.agents?.length) return [];
    return agentsData.agents.map((a) => ({
      id: a.name,
      name: a.displayName,
      description: a.description,
      icon: AGENT_ICON[a.name] ?? 'Info',
      parent: a.parent ?? null,
      landingPage: a.landingPage ?? null,
      isSelectable: a.isSelectable,
      isPinned: a.isPinned ?? false,
    }));
  }, [agentsData]);

  const agentItems: GuardianAgent[] = useMemo(() => {
    if (!flat.length) return [];
    const byId = new Map(flat.map((a) => [a.id, { ...a, children: [] as GuardianAgent[] }]));
    const roots: GuardianAgent[] = [];
    for (const node of byId.values()) {
      const parentNode = node.parent ? byId.get(node.parent) : null;
      if (parentNode) {
        parentNode.children?.push(node);
      } else {
        roots.push(node);
      }
    }
    return roots;
  }, [flat]);

  const findAgent = useMemo(() => {
    const index = new Map(flat.map((a) => [a.id, a]));
    return (id: string) => index.get(id);
  }, [flat]);

  return { agentItems, status, findAgent };
};

